import { ResponseError } from "api/runtime";
import config from "config/config";
import Content, { ContentNS } from "content/content";
import ErrorBanner from "features/shared/components/banners/error-banner/ErrorBanner";
import SubmitButton from "features/shared/components/buttons/submit-button/SubmitButton";
import { TextInput } from "features/shared/components/text-input/TextInput";
import { getSessionRelativeUrl } from "features/shared/hooks/navigation/useFlowRedirect";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import useGeneralAnalytics from "features/shared/hooks/analytics/useGeneralAnalytics";
import PageHeader from "features/shared/styles/page-header/PageHeader.styled";
import Body from "features/shared/styles/typography/Body.styled";
import { TertiaryHeader } from "features/shared/styles/typography/TertiaryHeader.styled";
import Logging from "logging";
import { FC, useState } from "react";
import DataProvider from "services/data-provider/data-provider";
import { executeRecaptcha, isRecaptchaEnabled } from "services/recaptcha/recaptcha";
import { useAppStore } from "store/store";

import StyledLoginForm from "./styles/StyledLoginForm.styled";
import StyledLoginFormContainer from "./styles/StyledLoginFormContainer.styled";
import AnalyticsService from "services/analytics/analytics";
import useMutatedPagePath from "features/shared/hooks/analytics/useMutatedPagePath";
import { SessionIneligibilityReasonFromApi } from "models/session-ineligibility-reason";
import DataProviderGeneralException, {
  DataProviderExceptionType,
} from "services/data-provider/data-provider-errors";

const LoginForm: FC = () => {
  useGeneralAnalytics();

  const locale = useAppStore((state) => state.locale);

  const navigate = useNavigateWithQuery();
  const updateSession = useAppStore((state) => state.updateSession);

  const setReturnUrl = useAppStore((state) => state.setReturnUrl);

  const [path, title, pageLocation] = useMutatedPagePath();

  const textUI = (s: string) => Content.shared.t(s, { ns: ContentNS.ui });

  const [isLoading, setIsLoading] = useState(false);
  const [haveSubmittedAtLeastOnce, setHaveSubmittedAtLeastOnce] =
    useState(false);

  const [bookingId, setBookingId] = useState("");
  const [surname, setSurname] = useState("");

  const handleBookingInputChange = (value: string) => {
    setBookingId(value.toUpperCase());
  };

  const handleSurnameInputChange = (value: string) => {
    setSurname(value.toUpperCase());
  };

  const [isSessionLocked, setSessionIsLocked] = useState(false);
  const [isBookingNotFound, setIsBookingNotFound] = useState(false);
  const [isUnknownError, setIsUnknownError] = useState(false);
  const [isRecaptchaError, setIsRecaptchaError] = useState(false);

  const setIneligibilityReason = useAppStore(
    (state) => state.setIneligibilityReason
  );

  const bookingError =
    bookingId.trim() === "" && haveSubmittedAtLeastOnce
      ? textUI("login-form.validation-errors.pnr")
      : "";

  const surnameError =
    surname.trim() === "" && haveSubmittedAtLeastOnce
      ? textUI("login-form.validation-errors.surname")
      : "";

  const errorTitle = (() => {
    if (isBookingNotFound) {
      return textUI("login-form.process-errors.not-found.title");
    }
    if (isSessionLocked) {
      return textUI("login-form.process-errors.session-locked.title");
    }
    if (isRecaptchaError) {
      return textUI("login-form.process-errors.recaptcha.title");
    }
    return textUI("login-form.process-errors.unknown.title");
  })();

  const errorDescription = (() => {
    if (isBookingNotFound) {
      return textUI("login-form.process-errors.not-found.description");
    }
    if (isSessionLocked) {
      return textUI("login-form.process-errors.session-locked.description");
    }
    if (isRecaptchaError) {
      return textUI("login-form.process-errors.recaptcha.description");
    }
    return textUI("login-form.process-errors.unknown.description");
  })();

  function validateForm(): boolean {
    return bookingId.trim() !== "" && surname.trim() !== "";
  }

  async function onSubmit() {
    if (isLoading) {
      return;
    }

    setHaveSubmittedAtLeastOnce(true);

    if (!validateForm()) {
      AnalyticsService.shared.trackEvent(
        "login_form_validation_failed",
        {
          page_path: path,
          content_group: path,
          page_title: title,
          page_location: pageLocation,
        },
        false // isScanRelated
      );
      return;
    }

    setIsLoading(true);

    // clear errors
    setIsBookingNotFound(false);
    setIsUnknownError(false);
    setSessionIsLocked(false);
    setIsRecaptchaError(false);

    let captchaToken = "";
    if (isRecaptchaEnabled()) {
      try {
        captchaToken = await executeRecaptcha();
      } catch (e) {
        Logging.log(
          "login",
          "missing",
          `RECAPTCHA error: ${e instanceof Error ? e.message : String(e)}`
        );
        setIsRecaptchaError(true);
        setIsLoading(false);
        return;
      }
    }

    if (isRecaptchaEnabled() && !captchaToken) {
      Logging.log("login", "missing", "captchaToken is null or invalid");
      setIsRecaptchaError(true);
      setIsLoading(false);
      return;
    }

    DataProvider.init(undefined, locale, null);

    DataProvider.shared
      .auth(bookingId, surname, captchaToken)
      .then(async (res) => {
        // success
        AnalyticsService.shared.setSessionId(res.sessionId);

        AnalyticsService.shared.trackEvent(
          "login_success",
          {
            page_path: path,
            content_group: path,
            page_title: title,
            page_location: pageLocation,
          },
          false // isScanRelated
        );

        DataProvider.init(res.paxCheckToken, locale, null);

        setReturnUrl(res.returnUrl || null);

        updateSession(await DataProvider.shared.getSession(res.sessionId));

        // navigate to session root (eg session start),
        // it will know what to do and where to navigate to further
        navigate({
          pathname: getSessionRelativeUrl(res.sessionId),
          search: `?pax_check_token=${res.paxCheckToken}`,
        });
      })
      .catch((e) => {
        if (e instanceof ResponseError && e.response.status === 401) {
          // unauthorised
          setIsRecaptchaError(true);
          AnalyticsService.shared.trackEvent(
            "login_captcha_failed",
            {
              page_path: path,
              content_group: path,
              page_title: title,
              page_location: pageLocation,
            },
            false // isScanRelated
          );
        } else if (
          e instanceof DataProviderGeneralException &&
          e.type !== DataProviderExceptionType.Unknown &&
          e.type !== DataProviderExceptionType.BadRequest
        ) {
          // api "general" error
          switch (e.type) {
            case DataProviderExceptionType.NotProcessable:
            case DataProviderExceptionType.NotEligible:
              // not processable or not eligible
              AnalyticsService.shared.trackEvent(
                "login_booking_ineligible",
                {
                  page_path: path,
                  content_group: path,
                  page_title: title,
                  page_location: pageLocation,
                },
                false // isScanRelated
              );

              setIneligibilityReason(
                SessionIneligibilityReasonFromApi(e.message)
              );
              navigate({
                pathname: `/ineligible`,
              });
              break;

            case DataProviderExceptionType.NotFound:
              // booking not found
              AnalyticsService.shared.trackEvent(
                "login_booking_not_found",
                {
                  page_path: path,
                  content_group: path,
                  page_title: title,
                  page_location: pageLocation,
                },
                false // isScanRelated
              );
              setIsBookingNotFound(true);
              break;

            case DataProviderExceptionType.SessionIsFinalised:
              setIsBookingNotFound(true);
              break;

            case DataProviderExceptionType.SessionIsLocked:
              setSessionIsLocked(true);
              break;
          }
        } else {
          // something else
          setIsUnknownError(true);
          Logging.log("login", "missing", e.message);
        }

        setIsLoading(false);
      });
  }

  if (config.isIndexLoginDissalowed) {
    return null;
  } else {
    return (
      <StyledLoginFormContainer>
        {isBookingNotFound ||
        isUnknownError ||
        isSessionLocked ||
        isRecaptchaError ? (
          <ErrorBanner title={errorTitle} descriptions={[errorDescription]} />
        ) : null}
        <StyledLoginForm>
          <PageHeader>
            <TertiaryHeader>{textUI("login-form.title")}</TertiaryHeader>
            <Body>{textUI("login-form.subtitle")}</Body>
          </PageHeader>
          <TextInput
            id="bookingId"
            value={bookingId}
            label={textUI("login-form.pnr-label")}
            error={bookingError}
            onChange={handleBookingInputChange}
          />
          <TextInput
            id="surname"
            value={surname}
            label={textUI("login-form.surname-label")}
            error={surnameError}
            onChange={handleSurnameInputChange}
          />
          <SubmitButton onClick={onSubmit} isLoading={isLoading}>
            {textUI("login-form.login-button")}
          </SubmitButton>
        </StyledLoginForm>
      </StyledLoginFormContainer>
    );
  }
};

export default LoginForm;
